import { ContactMethod } from '../enums/contact-method.enum';
import { ContactType } from '../enums/contact-type.enum';
import { DayOfWeek } from '../enums/day-of-week.enum';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import 'moment-duration-format';
import { HIPAAConsent } from '../enums/HIPAA-consent.enum';
import { Period } from '../enums/period.enum';
import { QueueType } from '../enums/queue-type.enum';
import { Roles } from '../enums/roles.enum';
import { IContactData } from '../interfaces/contact-data.interface';
import DOMPurify from 'dompurify';
import { isBlank } from '../utilities/empty-checks';
import { QueueTypeUrlSuffixes } from '../enums/queue-type-url-suffixes.enum';

export const getAdvancedEmailValidator = (): Validators => {
  return Validators.pattern('[a-zA-Z0-9.+-_]{1,}@[a-zA-Z0-9.-]{2,}[.]{1}[a-zA-Z0-9]{2,}');
};

export const getUserRole = (user) => {
  if (user.is_system_admin) {
    return Roles.SystemAdmin;
  } else if (user.is_program_admin) {
    return Roles.ProgramAdmin;
  } else if (user.is_agent_all) {
    return Roles.AgentAll;
  } else if (user.is_agent_restricted) {
    return Roles.AgentRestricted;
  } else {
    return null;
  }
};

export const isThisWeek = (date) => {
  const now = new Date();

  const weekDay = (now.getDay() + 6) % 7; // Make sure Sunday is 6, not 0
  const monthDay = now.getDate();
  const mondayThisWeek = monthDay - weekDay;

  const startOfThisWeek = new Date(+now);
  startOfThisWeek.setDate(mondayThisWeek);
  startOfThisWeek.setHours(0, 0, 0, 0);

  const startOfNextWeek = new Date(+startOfThisWeek);
  startOfNextWeek.setDate(mondayThisWeek + 7);

  return date >= startOfThisWeek && date < startOfNextWeek;
};

export const enumToArray = (enumValue) => {
  return Object.entries(enumValue)
    .filter((item) => !isNaN(item[0] as any))
    .map((item) => item[1]) as string[];
};

export const enumToObjectReverse = (enumValue) => {
  const enumParsed = Object.values(enumValue);
  const values = enumParsed.slice(0, enumParsed.length / 2) || [];
  const keys = enumParsed.slice(enumParsed.length / 2) || [];

  return keys.reduce((o: object, k: any, i: number) => ({...o, [k]: values[i]}), {});
};

export const setAgeColor = (date: string): string => {
  const age = getMinutesBetweenDates(date);

  if (age < 120) {
    return 'green';
  } else if (age >= 120 && age < 240) {
    return 'yellow';
  } else {
    return 'red';
  }
};

export const setAge = (date: string): string => {
  const age = getMinutesBetweenDates(date);

  if (age > 60 * 24) {
    return Math.floor(age / 60 / 24) + ' d';
  } else if (age > 60) {
    return Math.floor(age / 60) + ' hr ' + Math.round(age % 60) + ' min';
  } else {
    return Math.round(age) + ' min';
  }
};

export const noWhitespaceValidator = (control: FormControl) => {
  const isWhitespace = (control.value || '').trim().length === 0;
  const isValid = !isWhitespace;
  return isValid ? null : {whitespace: true};
};

export const phoneValidator = (control: FormControl) => {
  const match =
    !control.value ||
    (control.value &&
      (control.value.match(/^(\d{1})(\d{3})(\d{3})(\d{4,})$/) ||
        control.value.match(/^(\d{3})(\d{3})(\d{4,})$/)));
  return match ? null : {phoneFormat: true};
};

export const numberValidator = (control: FormControl) => {
  if (isBlank(control.value)) {
    return null;
  } // don`t need to validate empty values

  const cleaned = control.value !== null ? control.value + '' : '';
  const match = !cleaned || (cleaned && cleaned.match(/^([0-9]*)$/));
  return match ? null : {number: true};
};

export const ssnValidator = (control: FormControl) => {
  const match = !control.value || (control.value && control.value.match(/^(\d{3})(\d{2})(\d{4})$/));
  return match ? null : {ssnFormat: true};
};

export const greaterThanTodayValidator = (control: FormControl) => {
  let val = control.value;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (!(val instanceof Date)) {
    val = new Date(val);
  }
  return val && val >= today ? null : {greaterThanToday: true};
};

export const isPositive = (control: FormControl) => {
  const cleaned = control.value !== null ? control.value + '' : 0;
  const match = Number(cleaned) > 0;
  return match ? null : {positive: true};
};

export const isFilled = (control: FormControl) => {
  const cleaned = control.value !== null ? control.value + '' : '';
  return cleaned !== null && cleaned !== '' ? null : {empty: true};
};

export const getToken = () => {
  const token = localStorage.getItem('okta-token-storage');
  return JSON.parse(token)?.accessToken?.value;
};

export const toSearchString = (value: string) => {
  return value.replace(/\ /g, '').toLowerCase();
};

export const isIntakeQueue = (queueType: QueueType): boolean => {
  return queueType === QueueType.identification || queueType === QueueType.dataEntry;
};

export const getQueueTypeFromUrlSuffix = (urlSuffix: string): QueueType => {
  switch (urlSuffix) {
    case QueueTypeUrlSuffixes.supervisor:
      return QueueType.supervisor;
    case QueueTypeUrlSuffixes.identification:
      return QueueType.identification;
    case QueueTypeUrlSuffixes.dataEntry:
      return QueueType.dataEntry;
    case QueueTypeUrlSuffixes.supervisorIdentification:
      return QueueType.identification;
    default:
      return undefined;
  }
};

export const getUrlSuffixFromQueueType = (queueType: QueueType): QueueTypeUrlSuffixes => {
  switch (queueType) {
    case QueueType.supervisor:
      return QueueTypeUrlSuffixes.supervisor;
    case QueueType.identification:
      return QueueTypeUrlSuffixes.identification;
    case QueueType.dataEntry:
      return QueueTypeUrlSuffixes.dataEntry;
    default:
      return undefined;
  }
};

export const setIndicatorColor = (value) => {
  if (value < 50) {
    return 'orange';
  } else if (value >= 50 && value < 75) {
    return 'yellow';
  } else if (value >= 75 && value < 100) {
    return 'blue';
  } else {
    return 'green';
  }
};

export const getFullAddress = (address) => {
  if (address) {
    const addressArray = [];
    if (address.streetAddress) {
      addressArray.push(address.streetAddress);
    }
    if (address.city) {
      addressArray.push(address.city);
    }
    if (address.state) {
      addressArray.push(address.state);
    }
    if (address.zipCode) {
      addressArray.push(address.zipCode);
    }
    return addressArray.slice(0, -1).join(', ') + ' ' + addressArray.slice(-1);
  }
  return '';
};

export const getDateString = (date, format) => {
  if (date) {
    return moment(date).format(format);
  }
  return '';
};

export const getDateObject = (ISO: string): Date => {
  return new Date(ISO || '');
};

export const expiredFile = (utc: string): boolean => {
  return getDateObject(utc).getTime() < new Date().getTime();
};

export const getAgeString = (date) => {
  if (date) {
    const diff = getDateDiff(new Date(), date, 'months', false);
    if (diff < 12) {
      return getDurationString(diff, 'months', 'M [month] [old]', {
        trunc: true
      });
    }
    return getDurationString(diff, 'months', 'y [year] [old]', {
      trunc: true
    });
  }
  return '';
};

export const formatPhone = (input) => {
  if (!input) {
    return '';
  }

  let intlCode = '';
  const cleaned = input?.toString().replace(/\D/g, '');
  let match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4,})$/);
  if (match) {
    intlCode = '+' + match[1] + ' ';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  } else {
    match = cleaned.match(/^(\d{3})(\d{3})(\d{4,})$/);
    if (match) {
      return [intlCode, '(', match[1], ') ', match[2], '-', match[3]].join('');
    }
  }
  return cleaned;
};

export const getDateDiff = (date1, date2, param, decimal = false) => {
  const a = moment(date1);
  const b = moment(date2);
  return a.diff(b, param, decimal);
};

export const interpolateTemplate = (text, obj) => {
  if (text) {
    for (const property in obj) {
      if (property) {
        const re = new RegExp(`{{${property}}}`, 'g');
        text = text.replace(re, obj[property]);
      }
    }

    return text;
  }
  return '';
};

export const getDurationDays = (val: number = 0) => {
  if (val <= 2 * 24) {
    return val.toFixed(val % 1 === 0 ? 0 : 1) + ' Hour' + (val > 1 ? 's' : '');
  } else {
    return (val / 24).toFixed(val % 1 === 0 ? 0 : 1) + ' Day' + (val > 1 ? 's' : '');
  }
};

export const getDurationString = (val, param, template, options) => {
  return moment.duration(val, param).format(template, options);
};

export const getPhaseStart = (phaseTasks) => {
  if (phaseTasks && phaseTasks.length > 0) {
    const bvTasksByCreated = phaseTasks.sort((x, y) => x.createDate > y.createDate);
    return bvTasksByCreated[0].createDate;
  }
  return '';
};

export const getPhaseCompleted = (phaseTasks) => {
  if (phaseTasks && phaseTasks.length > 0) {
    if (phaseTasks.find(x => x.outcome === null)) {
      return '';
    }
    const bvTasksByCompleted = phaseTasks.sort((x, y) => x.completed < y.completed);
    return bvTasksByCompleted[0].completed;
  }
  return '';
};

export const getPhaseSpent = (tasks) => {
  if (tasks && tasks.length > 0) {
    const reducer = (accumulator, currentValue) => {
      const first = currentValue.createDate;
      const second = currentValue.completed || new Date();
      const diff = getDateDiff(second, first, 'hours', true);
      return accumulator + diff;
    };
    return tasks.reduce(reducer, 0);
  }
  return 0;
};

export const deepCopy = (oldObj: any) => {
  let newObj = oldObj;
  if (oldObj && typeof oldObj === 'object') {
    if (oldObj instanceof Date) {
      return new Date(oldObj.getTime());
    }
    newObj = Object.prototype.toString.call(oldObj) === '[object Array]' ? [] : {};
    for (const i in oldObj) {
      if (oldObj.hasOwnProperty(i)) {
        newObj[i] = deepCopy(oldObj[i]);
      }
    }
  }
  return newObj;
};

export const deepEqual = (obj1: object, obj2: object) => {
  if (obj1 === obj2) {
    return true;
  }

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  const keysA = Object.keys(obj1);
  const keysB = Object.keys(obj2);

  if (keysA.length !== keysB.length) {
    return false;
  }

  let result = true;

  keysA.forEach((key) => {
    if (!keysB.includes(key)) {
      result = false;
    }

    if (
      typeof obj1[key] === 'function' ||
      typeof obj2[key] === 'function'
    ) {
      if (obj1[key].toString() !== obj2[key].toString()) {
        result = false;
      }
    }

    if (!deepEqual(obj1[key], obj2[key])) {
      result = false;
    }
  });

  return result;
};

export const addSuggestedCase = (selectedCase) => {
  if (selectedCase && selectedCase.id) {
    const key = 'suggested_cases';
    const numberOfItems = 4;
    const suggestedCasesItem = localStorage.getItem(key);
    const cases = suggestedCasesItem ? JSON.parse(suggestedCasesItem) : [];
    const index = cases.findIndex(x => x.id === selectedCase.id);
    const patientName = `${selectedCase.patient?.firstName || ''} ${selectedCase.patient?.lastName || ''}`.trim();

    if (index >= 0) {
      cases.splice(index, 1);
    }
    cases.unshift({
      id: selectedCase.id,
      patient: patientName
    });

    const result = cases.slice(0, numberOfItems);
    localStorage.setItem(key, JSON.stringify(result));
  }
};

export const getSuggestedCases = () => {
  const key = 'suggested_cases';
  const item = localStorage.getItem(key);
  if (item) {
    return JSON.parse(item);
  }
  return [];
};

export const addPeriod = (date, period, param) => {
  return moment(date).add(period, param).format();
};

export const getContactInfos = (form: FormGroup): IContactData[] => {
  const contactInfos = [];

  if (form.value.officePhone) {
    contactInfos.push({
      name: '',
      contactString: ('' + form.value.officePhone).replace(/\D/g, ''),
      primary: true,
      contactMethod: ContactMethod.phone,
      contactType: ContactType.work,
    });
  }

  if (form.value.officeFax) {
    contactInfos.push({
      name: '',
      contactString: ('' + form.value.officeFax).replace(/\D/g, ''),
      primary: true,
      contactMethod: ContactMethod.fax,
      contactType: ContactType.work,
    });
  }

  if (form.value.officeEmail) {
    contactInfos.push({
      name: form.value.officeName,
      contactString: form.value.officeEmail,
      primary: true,
      contactMethod: ContactMethod.email,
      contactType: ContactType.work,
    });
  }

  return contactInfos;
};

export const getTimeFormat = (date: Date): string => {
  const hours = date.getHours();
  const min = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  const hoursString = hours % 12 ? hours % 12 : '12';
  const minString = min < 10 ? '0' + min : min;

  return hoursString + ':' + minString + ' ' + ampm;
};

// convert string '7:30 PM' to '19:30'
export const get24FromAmpmTime = (time: string): string => {
  let hours = Number(time.match(/^(\d+)/)[1]);
  const minutes = Number(time.match(/:(\d+)/)[1]);
  const AMPM = time.match(/\s(.*)$/)[1];

  if (AMPM.toLowerCase() === 'pm' && hours < 12) {
    hours = hours + 12;
  }
  if (AMPM.toLowerCase() === 'am' && hours === 12) {
    hours = hours - 12;
  }

  let sHours = hours.toString();
  let sMinutes = minutes.toString();

  if (hours < 10) {
    sHours = '0' + sHours;
  }
  if (minutes < 10) {
    sMinutes = '0' + sMinutes;
  }

  return sHours + ':' + sMinutes;
};

export const getConsentValue = (consentObj) => {
  return consentObj?.consentIsOnFile === true
    ? HIPAAConsent.Yes
    : (consentObj?.consentIsOnFile === false ? HIPAAConsent.No : null);
};

export const getISOString = (date: Date): string => {
  return date ? moment(date).format('YYYY-MM-DDT00:00:00.000') + 'Z' : null;
};

export const getContactInfoObject = (contactMethod: ContactMethod, value: string): IContactData => {
  return {
    contactMethod,
    contactString: value,
    contactType: ContactType.work,
    name: '',
    primary: true,
    externalSourceContactId: null
  };
};

export const getContactInfoString = (
  contactInfos: IContactData[],
  method: ContactMethod,
  type: ContactType = ContactType.work,
): string => {
  contactInfos.forEach((el: IContactData) => {
    if (el.contactMethod === method && el.contactType === type) {
      return el.contactString;
    }
  });

  return '';
};

export const getPropertyByPath = (obj, path) => {
  let current = obj;
  const tokens = path.split('.');
  // tslint:disable-next-line
  for (let i = 0; i < tokens.length; i++) {
    if (!current[tokens[i]]) {
      return null;
    }
    current = current[tokens[i]];
  }

  return current;
};

export const orderObjProperties = (order, item) => {
  const columns = [];
  for (const col of order) {
    columns.push(getPropertyByPath(item, col));
  }
  return columns;
};

export const downloadFile = (file, name) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.setAttribute('style', 'display:none;');
  document.body.appendChild(a);
  a.download = DOMPurify.sanitize(name + getExtention(file.type));
  a.href = DOMPurify.sanitize(url);
  a.target = '_blank';
  a.click();
  document.body.removeChild(a);
};

export const getExtention = (mimeType) => {
  switch (mimeType) {
    case 'text/csv':
      return '.csv';
    case 'application/pdf':
      return '.pdf';
    default:
      return '';
  }
};

export const getReportFileName = (report) => {
  return `${report?.name || 'CaseDetailReport'}-${moment().format('MM_DD_YYYY')}`;
};

export const getDayOfWeekShortName = (date) => {
  const dayOfWeek = date.getDay();
  const dayOfWeekName = moment(date).format('dd');
  if (dayOfWeek !== DayOfWeek.thursday) {
    return dayOfWeekName.substr(0, 1);
  }

  return dayOfWeekName;
};

export const getPeriodStartDate = (period, customStart) => {
  switch (period) {
    case Period['All Time']:
      return null;
    case Period['This Week']:
      return moment().startOf('week').toDate();
    case Period['Previous Week']:
      return moment().add(-1, 'week').startOf('week').toDate();
    case Period['This Month']:
      return moment().startOf('month').toDate();
    case Period['Previous Month']:
      return moment().add(-1, 'month').startOf('month').toDate();
    case Period['This Quarter']:
      return moment().startOf('quarter').toDate();
    case Period['Previous Quarter']:
      return moment().add(-1, 'quarter').startOf('quarter').toDate();
    case Period['Previous Year']:
      return moment().add(-1, 'year').startOf('year');
    case Period['This Year']:
      return moment().startOf('year');
    case Period['Custom Date Range']:
      return new Date(customStart);
    default:
      return null;
  }
};

export const getPeriodEndDate = (period, customEnd) => {
  switch (period) {
    case Period['All Time']:
      return null;
    case Period['This Week']:
      return new Date();
    case Period['Previous Week']:
      return moment().add(-1, 'week').endOf('week').toDate();
    case Period['This Month']:
      return new Date();
    case Period['Previous Month']:
      return moment().add(-1, 'month').endOf('month').toDate();
    case Period['This Quarter']:
      return new Date();
    case Period['Previous Quarter']:
      return moment().add(-1, 'quarter').endOf('quarter').toDate();
    case Period['Previous Year']:
      return moment().add(-1, 'year').endOf('year');
    case Period['This Year']:
      return new Date();
    case Period['Custom Date Range']:
      return new Date(customEnd);
    default:
      return null;
  }
};

export const getMinutesBetweenDates = (date: string): number => {
  const currentDate = new Date();
  const taskCreatedDate = new Date(date);
  const diff = currentDate.getTime() - taskCreatedDate.getTime();
  return diff / 60000;
};

export const getFacilityFormObject = (facility): object => {
  const phone = facility ? facility.contactInfos.find((x) => x.contactMethod === ContactMethod.phone) : null;
  const fax = facility ? facility.contactInfos.find((x) => x.contactMethod === ContactMethod.fax) : null;
  const email = facility ? facility.contactInfos.find((x) => x.contactMethod === ContactMethod.email) : null;

  return {
    facilityName: facility ? facility.name : '',
    address: facility ? facility.address.streetAddress : '',
    tax: facility ? facility.groupTaxId : '',
    zipCode: facility ? facility.address.zipCode : '',
    officePhone: phone ? phone.contactString : '',
    officeFax: fax ? fax.contactString : '',
    officeName: email ? email.name : '',
    officeEmail: email ? email.contactString : '',
  };
};

export const flatArrays = (arr: any[]) => arr.reduce(
  (a, b) => a.concat(Array.isArray(b) ? flatArrays(b) : b), []
);

export const isIncludedInTimeFrame = (from: string, to: string): boolean => {
  if (!from || !to) {
    return false;
  }

  const today = new Date();

  return new Date(today).getTime() > new Date(from).getTime() && new Date(today).getTime() < new Date(to).getTime();
};
